import { createRouter, createWebHistory } from 'vue-router'

import PageNotFound from '../views/PageNotFound.vue'
import WorkModal from '../components/WorkModal.vue'
import HomeGate from '../views/HomeGate.vue'
import { store, actions, authReadyPromise } from '@/store'
import { resetFirstThumbMark } from '@/components/gallery/GalleryItem.vue'
import { reset as resetPerfMarks } from '@/utils/perfMarks'
import { isTenantHost, hostnameHasTenant, PLATFORM_HOSTS, tenantReadyPromise } from '@/utils/tenantUtils'
import { ADMIN_DEFAULT_ROUTE } from './constants'

export { ADMIN_DEFAULT_ROUTE }

// Reserved route slugs that cannot be used for custom pages
export const RESERVED_ROUTES = [
  'login',
  'signup',
  'admin',
  'gallery',
  '_home'
]

// Shared beforeEnter guard for work routes
const workBeforeEnter = async (to, from, next) => {
  const slug = to.params.workSlug
  if (from.matched.length === 0) {
    // Direct URL hit — kick off fetch immediately, don't block navigation
    const work = await actions.fetchWorkBySlug(slug)
    if (!work) {
      to.meta.workNotFound = true
      to.meta.requestedSlug = slug
    }
  }
  next()
}

const routes = [
  // --- Public routes ---
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/Login.vue')
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('../views/SignUp.vue')
  },
  {
    path: '/legal/terms',
    name: 'terms',
    component: () => import('../views/LegalTerms.vue'),
    meta: { platformOnly: true }
  },
  // --- Visitor routes ---
  {
    path: '/',
    name: 'Home',
    component: HomeGate
  },
  // Legacy redirect: for old bookmarks/indexed links to the marketing page.
  {
    path: '/welcome',
    redirect: '/'
  },
  // Legacy redirect: /works/:slug → /gallery/:workSlug
  {
    path: '/works/:slug',
    redirect: to => ({ name: 'work', params: { workSlug: to.params.slug } })
  },

  // --- Admin routes (require login or admin role) ---
  {
    path: '/admin/artworks',
    name: 'PersonalGalleryAdmin',
    component: () => import('../views/PersonalGalleryAdmin.vue'),
    meta: { requiresLogin: true }
  },
  {
    path: '/admin/inbox',
    name: 'inbox',
    component: () => import('../views/Inbox.vue'),
    meta: { requiresLogin: true }
  },
  {
    path: '/admin/messages',
    redirect: '/admin/inbox'
  },
  {
    path: '/admin/pages',
    name: 'CustomPages',
    component: () => import('../views/CustomPages.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/admin/pages/:slug/edit',
    name: 'CustomPageEditor',
    component: () => import('../views/CustomPageEditor.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/admin/contacts',
    redirect: '/admin/inbox'
  },
  {
    path: '/admin/profile',
    name: 'profile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresLogin: true }
  },

  {
    path: '/admin/settings',
    redirect: '/admin/settings/site'
  },
  {
    path: '/admin/settings/:section',
    name: 'settings',
    component: () => import('../views/Settings.vue'),
    meta: { requiresAdmin: true }
  },
  {
    // Any unknown /admin or /admin/* path -> guarded admin default (never the visitor 404).
    path: '/admin/:pathMatch(.*)*',
    redirect: ADMIN_DEFAULT_ROUTE
  },
  // --- Gallery work overlay: gallery stays mounted, WorkModal renders on top ---
  {
    path: '/:pageSlug?/gallery/:workSlug',
    name: 'work',
    components: {
      default: HomeGate,
      modal: WorkModal
    },
    props: {
      default: route => ({ slug: route.params.pageSlug || '_home' }),
      modal: true
    },
    beforeEnter: workBeforeEnter
  },

  // --- Dynamic custom page (must be after /admin/* and gallery routes) ---
  {
    path: '/:slug',
    name: 'CustomPage',
    component: HomeGate,
    props: true
  },

  // --- Catch-all ---
  {
    path: '/:pathMatch(.*)*',
    name: '404',
    component: PageNotFound
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.name === 'work' || from?.name === 'work') return false
    return { top: 0 }
  }
})

// Stale chunk after a deploy: reload once to the intended path. Flag guards against a loop.
router.onError((error, to) => {
  const isChunkError = /ChunkLoadError|Loading chunk \S+ failed|error loading dynamically imported module|Importing a module script failed/i.test(error?.message || '')
  if (isChunkError && !sessionStorage.getItem('chunkReloaded')) {
    sessionStorage.setItem('chunkReloaded', '1')
    window.location.assign(to?.fullPath || window.location.href)
  }
})
router.afterEach(() => {
  sessionStorage.removeItem('chunkReloaded')
})

// Routes on the platform host that logged-in users should not access
const GUEST_ONLY_ROUTE_NAMES = new Set(['login', 'signup', 'Home'])

router.beforeEach(async (to, from, next) => {
  resetFirstThumbMark()
  // Skip on initial navigation - it would wipe pre-router boot marks (tenant:resolve:start).
  if (from.matched.length > 0) resetPerfMarks()
  await tenantReadyPromise

  // Subdomain exists in DNS but Firestore couldn't resolve it to a tenant
  // (unknown subdomain, temp outage, etc.) → redirect to apex platform domain
  if (hostnameHasTenant() && !isTenantHost()) {
    const hostname = window.location.hostname
    const base = PLATFORM_HOSTS.find(h => hostname.endsWith('.' + h)) || 'vitrinis.com'
    const port = window.location.port ? ':' + window.location.port : ''
    window.location.href = `${window.location.protocol}//${base}${port}/`
    return
  }

  // Auth and legal routes on tenant subdomains → redirect to platform host
  const isAuthRoute = to.name === 'login' || to.name === 'signup'
  const isPlatformOnly = isAuthRoute || to.meta.platformOnly
  if (isPlatformOnly && isTenantHost()) {
    const hostname = window.location.hostname
    const base = PLATFORM_HOSTS.find(h => hostname.endsWith('.' + h)) || 'localhost'
    const port = window.location.port ? ':' + window.location.port : ''
    window.location.href = `${window.location.protocol}//${base}${port}${to.fullPath}`
    return
  }

  // Platform host: only auth, legal, admin routes, and Home (renders PlatformHome
  // via HomeGate) are valid
  if (!isTenantHost()) {
    const isPlatformRoute = to.name === 'Home'
      || isPlatformOnly
      || to.path.startsWith('/admin')
    if (!isPlatformRoute) {
      next('/')
      return
    }
  }

  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin)
  const requiresLogin = to.matched.some(record => record.meta.requiresLogin)
  // Guest-only routes: redirect logged-in users to admin (platform host only)
  const isGuestOnly = GUEST_ONLY_ROUTE_NAMES.has(to.name) && !isTenantHost()

  if (requiresAdmin || requiresLogin || isGuestOnly) {
    // Auth is only started when a route actually needs it
    actions.authCheckUser()
    await authReadyPromise

    if (isGuestOnly && store.user.uid) {
      next(ADMIN_DEFAULT_ROUTE)
    } else if (requiresAdmin && !store.user.isAdmin) {
      next('/')
    } else if (requiresLogin && !store.user.uid) {
      next('/')
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router
